import {
  Home,
  BarChart,
  ChildCare,
  School,
  CalendarMonth,
  CalendarToday,
  People,
  Phone,
  Warning,
  Visibility,
  Folder,
  Language,
} from "@mui/icons-material";
import { Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, SvgIconProps } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { useServicePermissions, PartnerPortalAcl } from "../../hooks";
import { useAppSelector } from "../../store/hooks";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

interface MenuItem {
  text: string;
  icon: React.ComponentType<SvgIconProps>;
  path: string;
  requiredPermission?: PartnerPortalAcl; // Optional permission requirement
}

const menuItems: MenuItem[] = [
  { text: "Dashboard", icon: Home, path: "/dashboard" },
  { text: "Attendance Trends", icon: BarChart, path: "/attendance" },
  { text: "Rolls & Attendance", icon: ChildCare, path: "/bookings" },
  { text: "Team members", icon: School, path: "/educators" },
  {
    text: "Term Dates & PFDs",
    icon: CalendarMonth,
    path: "/key-dates",
  },
  //{ text: "Rocketeers", icon: CalendarToday, path: "/rocketeers" },
  {
    text: "School Contacts",
    icon: People,
    path: "/school-contacts",
  },
  { text: "Escalation path", icon: Phone, path: "/escalation-path" },
  { text: "Operational Information", icon: Warning, path: "/operational-information" },
  { text: "Visit summary", icon: Visibility, path: "/visit-summary" },
  { text: "Documents", icon: Folder, path: "/documents" },
  { text: "Website content", icon: Language, path: "/website-content" },
];

const SIDEBAR_WIDTH = 280;

function Sidebar({ open, onClose, isMobile = false }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedService = useAppSelector((state) => state.service.selectedService);
  const { hasServicePermission, hasGlobalPermission } = useServicePermissions();

  // Filter menu items based on permissions
  // Logic matches UserHasServicePermissionAttribute.cs:
  // 1. First check if user has the ACL in global acl claim
  // 2. Then check service-specific permission in partner_service_acls claim
  const visibleMenuItems = menuItems.filter((item) => {
    // If no permission is required, show the item
    if (!item.requiredPermission) {
      return true;
    }

    // First check if user has the permission globally (in acl claim)
    if (hasGlobalPermission(item.requiredPermission)) {
      return true;
    }

    // If permission is required but no service is selected, hide the item
    if (!selectedService?.serviceId) {
      return false;
    }
    // Check if user has the required permission for the selected service
    return hasServicePermission(selectedService.serviceId, item.requiredPermission);
  });

  const handleItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        maxWidth: SIDEBAR_WIDTH,
        height: "100%",
        backgroundColor: "#5CE5BB",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      {/* Header with CA Logo */}
      <Box
        sx={{
          padding: "24px 20px 16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "48px",
        }}
      >
        <Box
          component="a"
          href="/dashboard"
          className="caLogo"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: "48px",
            padding: "8px 12px",
            textDecoration: "none",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <img
            src="/assets/images/desktop_logo.svg"
            alt="Camp Australia"
            style={{
              height: "100%",
              width: "auto",
              maxWidth: "228px",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      {/* Menu Items */}
      <List
        sx={{
          flex: 1,
          padding: "16px 0 16px 16px", // Left padding only, no right padding
          "& .MuiListItemButton-root": {
            paddingX: "20px",
            paddingY: "12px", // Increased from 4px to 12px for 48px touch target
            margin: "2px 0",
            borderRadius: "20px 0 0 20px", // Only left-side rounded corners
            color: "#2c3e50",
            minHeight: "48px", // Increased from 36px to 48px for mobile touch targets
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            "&.active": {
              backgroundColor: "#CEF7EA", // Light mint green background for selected item
              color: "#2c3e50",
              fontWeight: 500,
              marginRight: "0", // Extend to the right edge
            },
          },
          "& .MuiListItemIcon-root": {
            minWidth: "40px",
          },
          "& .MuiListItemText-primary": {
            fontSize: "1rem", // 16px - mobile accessible font size
            fontWeight: 600,
            fontFamily: "ABC Whyte",
            color: "inherit",
            lineHeight: 1.5, // Better readability on mobile
          },
        }}
      >
        {visibleMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          const IconComponent = item.icon;

          return (
            <ListItemButton
              key={item.text}
              onClick={() => handleItemClick(item.path)}
              className={isActive ? "active" : ""}
            >
              <ListItemIcon>
                <IconComponent sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  if (isMobile) {
    // Mobile drawer: temporary overlay drawer that starts closed
    // User must tap the menu button to open it
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          zIndex: 1300, // Above AppBar (1100) for mobile
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
            border: "none",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop drawer: persistent drawer that starts open
  // User can toggle it using the menu button
  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: open ? SIDEBAR_WIDTH : 0,
        flexShrink: 0,
        display: { xs: "none", md: open ? "block" : "none" }, // Hide on mobile
        "& .MuiDrawer-paper": {
          width: SIDEBAR_WIDTH,
          maxWidth: SIDEBAR_WIDTH,
          boxSizing: "border-box",
          border: "none",
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          zIndex: 1200,
          overflow: "hidden",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export default Sidebar;
